import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useAppCatalogLocale } from '@/i18n/context';
import { CompanyRequestError } from '@/company/api';
import { formatCopy, useNamespaceCopy } from '@/i18n/ns';
import storefrontCopy, { type StorefrontCopyKey } from '@/i18n/ns/storefront';
import { StatusPill } from '@/components/ui/kit';
import { WorkspaceIcon } from '../icons';
import { useStorefrontApi, type RunStep, type StoreDetail } from './api';
import { ANSWERING_MODEL_LABELS } from '../model-choices.generated';
import { minutesSince, when } from './format';
import { runScopeText } from './store-line';
import { CHECKS_DETAIL, READ_MINUTES, RUN_READ } from './read-request';
import { failureText } from './use-storefront';
import { NoteSource } from './action-draft';
import { StoreProfileCard } from './store-profile';
import { InformationPages } from './information-pages';

const STEP_IDS = ['open', 'search', 'products', 'cart', 'chat', 'look', 'checks', 'ready'] as const;

// Reads that stopped on Poneva's side (the checks' model or browsers unavailable twice, a restart,
// a read the service lost, a start whose answer was lost): the shop did not cause them, and the
// words do not say it did.
const OUR_SIDE = new Set([
  'discovery_checks_unavailable',
  'server_restarted',
  'run_lost',
  'store_read_unavailable',
]);

/** Why a read stopped, what stays and what to do next. A live store keeps its assistant; the step
 * is named only when marked; Check again is named only when the server offers it. */
function stoppedText(
  store: StoreDetail,
  usable: boolean,
  marked: boolean,
  copy: Record<StorefrontCopyKey, string>,
  canCheckAgain = false,
): string {
  const ours = OUR_SIDE.has(store.run?.reason ?? '');
  const state = store.live
    ? ours
      ? copy.reasonOurSideLive
      : copy.reasonFailedLive
    : usable && store.draft
      ? formatCopy(ours ? copy.reasonOurSideKept : copy.reasonFailedKept, {
          version: String(store.draft.version),
        })
      : ours
        ? copy.reasonOurSide
        : copy.reasonFailed;
  return [
    state,
    marked ? copy.reasonStepMarked : '',
    canCheckAgain ? copy.reasonCheckAgain : ours ? copy.reasonTryLater : copy.reasonReadAgain,
  ]
    .filter(Boolean)
    .join(' ');
}

/** How long Check again stays busy after a started check when the page cannot show it yet. */
const RECHECK_SETTLE_MS = 3000;

export function SetupTab({
  store,
  onChanged,
  onTryWidget,
  onDesign,
  onKnowledge,
  onProfile,
  onPages,
  onUpdatePages,
  onMarket,
  onFixRead,
  notify,
}: {
  store: StoreDetail;
  onChanged: (store: StoreDetail) => void;
  onTryWidget: () => void;
  onDesign?: () => void;
  onKnowledge?: () => void;
  /** The store profile is added to and changed in Chat, like the notes. */
  onProfile?: () => void;
  onPages?: () => void;
  onUpdatePages?: () => void;
  /** Adding a market happens in Chat, which asks what to add. */
  onMarket?: () => void;
  /** A stopped read is steered in Chat: a new chat names the step that stopped, if one is marked. */
  onFixRead?: (step: string | null) => void;
  notify: (text: string) => void;
}) {
  const copy = useNamespaceCopy(storefrontCopy);
  const locale = useAppCatalogLocale() ?? 'en';
  const api = useStorefrontApi();
  const [stopping, setStopping] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const settleTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [confirming, setConfirming] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);
  const cancelConfirm = () => {
    setConfirming(false);
    requestAnimationFrame(() => opener.current?.focus());
  };
  const run = store.run;
  const stepName: Record<string, string> = {
    open: copy.stepOpen,
    search: copy.stepSearch,
    products: copy.stepProducts,
    cart: copy.stepCart,
    chat: copy.stepChat,
    look: copy.stepLook,
    checks: copy.stepChecks,
    ready: copy.stepReady,
  };
  // What a step is doing, in the reader's language. The box's own words for it are English and
  // stay with Chat; Setup says it from the step and the kind of read.
  const checking = run?.scope ? CHECKS_DETAIL[run.scope] : undefined;
  const stepDetail: Record<string, string> = {
    open: formatCopy(copy.stepDetailOpen, { domain: store.domain }),
    search: copy.stepDetailSearch,
    products: copy.stepDetailProducts,
    cart: copy.stepDetailCart,
    chat: copy.stepDetailChat,
    look: copy.stepDetailLook,
    checks: checking ? copy[checking] : copy.stepDetailChecks,
  };
  // The run decides this tab: a live store being read again shows the new run's progress.
  const runEnded = !run || ['ready', 'failed', 'cancelled', 'held'].includes(run.status);
  const failed =
    store.status === 'failed' ||
    (run !== null && ['failed', 'cancelled', 'held'].includes(run.status));
  const reading = !failed && (store.status === 'reading' || !runEnded);
  // A version shoppers can be shown exists (ready, already live, or a saved draft that a later read
  // of one part did not replace), whatever the latest read did.
  const usable =
    store.status === 'ready' || store.status === 'live' || Boolean(store.draft || store.live);
  // A stopped run leaves its current step marked running; show it stopped, not still spinning.
  const steps: RunStep[] = (
    run?.steps?.length
      ? run.steps
      : STEP_IDS.map((id) => ({ id, label: id, state: 'waiting' as const }))
  ).map((step) =>
    failed && step.state === 'running' ? { ...step, state: 'failed' as const } : step,
  );
  // Only the server decides whether the saved work can be checked again (run.checkAgain).
  const canCheckAgain = failed && run?.checkAgain === true;
  const stoppedStep = steps.find((step) => step.state === 'failed') ?? null;
  const failedText = stoppedText(store, usable, stoppedStep !== null, copy, canCheckAgain);
  const doneCount = steps.filter((step) => step.state === 'done').length;
  const current = steps.find((step) => step.state === 'running') ?? null;
  const stop = async () => {
    setStopping(true);
    try {
      await api.cancelRun(store.id);
      onChanged(store);
    } catch (failure) {
      notify(failureText(failure, copy, copy.actionFailed));
    } finally {
      setStopping(false);
    }
  };
  const retry = async () => {
    setConfirming(false);
    setRetrying(true);
    try {
      const { store: next } = await api.retryRun(store.id);
      onChanged(next);
    } catch (failure) {
      notify(failureText(failure, copy, copy.actionFailed));
    } finally {
      setRetrying(false);
    }
  };
  // No confirmation: checking the saved work again is not a new read of the store, and the status
  // sentence says that only the new checks use credits. Busy from the tap until the page shows the
  // started check, so a second tap cannot land in between.
  const checkAgain = async () => {
    setRechecking(true);
    try {
      const { store: next } = await api.recheckRun(store.id);
      onChanged(next);
      // If the page's re-read fails, the button returns after one poll interval; a second tap is
      // refused as moved on, which reads the store again.
      clearTimeout(settleTimer.current);
      settleTimer.current = setTimeout(() => setRechecking(false), RECHECK_SETTLE_MS);
    } catch (failure) {
      // Refused because the read moved on, such as a check already started from Chat: nothing to
      // try again, so no message; reading the store again shows where it is.
      const movedOn =
        failure instanceof CompanyRequestError && failure.code === 'checks_not_retryable';
      if (!movedOn) notify(failureText(failure, copy, copy.actionFailed));
      setRechecking(false);
      onChanged(store);
    }
  };
  useEffect(() => setRechecking(false), [run?.id, run?.status]);
  useEffect(() => () => clearTimeout(settleTimer.current), []);
  const stateWord: Record<RunStep['state'], string> = {
    done: copy.stepStateDone,
    running: copy.stepStateRunning,
    waiting: copy.stepStateWaiting,
    failed: copy.stepStateFailed,
  };
  // What the read found: first after a finished read; after a stopped one, under the marked steps.
  const foundSection =
    store.found && !reading ? (
      <section className="sf-found">
        <h2>{copy.foundTitle}</h2>
        <ul>
          <li>
            <WorkspaceIcon name="check" size={14} />
            {/* found.products counts the read's test search, not the catalogue: no number here. */}
            {copy.foundProducts}
          </li>
          {store.found.searchPath && (
            <li>
              <WorkspaceIcon name="check" size={14} />
              {copy.foundSearch}
            </li>
          )}
          {store.found.cart && (
            <li>
              <WorkspaceIcon name="check" size={14} />
              {copy.foundCart}
            </li>
          )}
          <li>
            <WorkspaceIcon name={store.found.chat ? 'check' : 'minus'} size={14} />
            {store.found.chat
              ? formatCopy(copy.foundChat, { vendor: store.found.chat.vendor })
              : copy.foundNoChat}
          </li>
          <li className="sf-found-look">
            <WorkspaceIcon name="check" size={14} />
            {copy.foundLook}
            <span className="sf-swatches" aria-hidden="true">
              {store.found.look.colours.slice(0, 4).map((colour) => (
                <i key={colour} style={{ background: colour }} />
              ))}
            </span>
          </li>
        </ul>
      </section>
    ) : null;
  const clock = (iso: string) =>
    new Date(iso).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    });
  const stepsSummary = formatCopy(copy.stepsRead, {
    when: run?.startedAt
      ? `${when(run.startedAt, locale)}${run.finishedAt ? `–${clock(run.finishedAt)}` : ''}`
      : '',
  });
  return (
    <div className="sf-setup">
      <section
        className="sf-now"
        data-state={reading ? 'running' : failed ? 'failed' : 'done'}
        aria-live="polite"
      >
        <span className="sf-now-mark" aria-hidden="true">
          {reading ? (
            <span className="sf-spin" />
          ) : (
            <WorkspaceIcon name={failed ? 'close' : 'check'} size={18} />
          )}
        </span>
        <span className="sf-now-text">
          <b data-testid="sf-now-title">
            {reading
              ? formatCopy(copy.nowRunning, {
                  step: stepName[current?.id ?? 'open'] ?? current?.label ?? '',
                })
              : failed
                ? copy.nowFailed
                : store.live && run?.finishedAt
                  ? formatCopy(copy.nowLastRead, { when: when(run.finishedAt, locale) })
                  : copy.nowReady}
          </b>
          <small>
            {reading
              ? [
                  runScopeText(run, store.markets, copy),
                  formatCopy(copy.nowProgress, {
                    n: String(Math.min(doneCount + 1, steps.length)),
                    total: String(steps.length),
                    minutes: String(minutesSince(run?.startedAt)),
                  }),
                  // How long such a read usually takes, so the minutes so far mean something. A
                  // check of saved work again has no confirmed duration, so it promises none.
                  !run?.checkOnly &&
                    formatCopy(copy.nowUsually, {
                      min: String(READ_MINUTES[RUN_READ[run?.scope ?? 'all'] ?? 'all'][0]),
                      max: String(READ_MINUTES[RUN_READ[run?.scope ?? 'all'] ?? 'all'][1]),
                    }),
                ]
                  .filter(Boolean)
                  .join(' · ')
              : failed
                ? run?.reason === 'discovery_cancelled'
                  ? copy.reasonCancelled
                  : run?.reason === 'discovery_timeout'
                    ? copy.reasonTimeout
                    : run?.reason === 'discovery_not_a_store'
                      ? copy.reasonNotAStore
                      : run?.reason === 'discovery_too_slow'
                        ? copy.reasonTooSlow
                        : failedText
                : copy.nowReadyBody}
          </small>
        </span>
      </section>

      {/* Designing while the first read runs; a store with a version already has its design. */}
      {reading && onDesign && !store.draft && !store.live && !run?.checkOnly && (
        <section className="sf-design-while" data-testid="sf-design-while">
          <div>
            <b>{copy.designWhileTitle}</b>
            <p>{copy.designWhileBody}</p>
          </div>
          <button
            className="ws-button primary"
            data-testid="sf-design-while-chat"
            onClick={onDesign}
          >
            {copy.designWithChat}
          </button>
        </section>
      )}

      {!failed && foundSection}
      <StepsFold done={!reading && !failed} summary={stepsSummary}>
        <ol className="sf-steps">
          {steps.map((step) => (
            <li key={step.id} className="sf-step" data-state={step.state} data-testid="sf-step">
              <span className="sf-step-mark" aria-hidden="true">
                {step.state === 'done' ? (
                  <WorkspaceIcon name="check" size={12} />
                ) : step.state === 'running' ? (
                  <span className="sf-spin small" />
                ) : step.state === 'failed' ? (
                  <WorkspaceIcon name="close" size={12} />
                ) : null}
              </span>
              <span className="sf-step-text">
                <b>{stepName[step.id] ?? step.label}</b>
                <span className="ws-sr-only">, {stateWord[step.state]}</span>
                {/* Work in progress says what it does; a finished step lets "What it found" speak. */}
                {stepDetail[step.id] && (step.state === 'running' || step.state === 'failed') && (
                  <small>{stepDetail[step.id]}</small>
                )}
              </span>
              {step.at && step.state === 'done' && (
                <span className="sf-step-at">{when(step.at, locale)}</span>
              )}
            </li>
          ))}
        </ol>
      </StepsFold>

      {run && (
        // What the person needs about the assistant: the model that answers shoppers (the model
        // and effort that read the store are ours to choose, so not shown) and who pays.
        <dl className="sf-facts">
          {store.assistantModel && (
            <div>
              <dt>{copy.factAnswers}</dt>
              <dd>{ANSWERING_MODEL_LABELS[store.assistantModel] ?? store.assistantModel}</dd>
            </div>
          )}
          <div>
            <dt>{copy.factPaid}</dt>
            <dd>{copy.paidCompany}</dd>
          </div>
        </dl>
      )}

      {failed && foundSection}

      {/* The store's markets, when it has more than one site or language: one assistant serves them all. */}
      {store.markets?.length ? (
        <section className="sf-knows sf-markets" data-testid="sf-markets">
          <div className="sf-knows-head">
            <div>
              <h2>{copy.marketsTitle}</h2>
              <p>{copy.marketsBody}</p>
            </div>
            {onMarket && (
              <button
                type="button"
                className="ws-button"
                data-testid="sf-market-chat"
                onClick={onMarket}
              >
                <WorkspaceIcon name="chat" size={14} />
                {copy.addMarketInChat}
              </button>
            )}
          </div>
          <ul className="sf-market-list">
            {store.markets.map((market) => (
              <li
                key={market.code}
                className="sf-market"
                data-status={market.status}
                data-testid="sf-market"
              >
                <span className="sf-cc">{market.code.toUpperCase()}</span>
                <span className="sf-market-text">
                  <b>
                    {market.name} <span>· {market.site}</span>
                  </b>
                  <small>
                    {[
                      languageNames(market.languages, locale),
                      market.currency,
                      market.checkedAt
                        ? formatCopy(copy.marketChecked, { when: when(market.checkedAt, locale) })
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </small>
                </span>
                <StatusPill
                  tone={
                    market.status === 'ready'
                      ? 'success'
                      : market.status === 'failed'
                        ? 'danger'
                        : 'neutral'
                  }
                >
                  {copy[`marketStatus.${market.status}` as StorefrontCopyKey]}
                </StatusPill>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {/* The notes it answers store questions from, chosen with Chat; the catalogue is always live. */}
      <section className="sf-knows" data-testid="sf-knowledge">
        <div className="sf-knows-head">
          <div>
            <h2>{copy.knowledgeTitle}</h2>
            <p>{copy.knowledgeBody}</p>
          </div>
          {onKnowledge && (
            <button
              type="button"
              className="ws-button"
              data-testid="sf-knowledge-chat"
              onClick={onKnowledge}
            >
              <WorkspaceIcon name="chat" size={14} />
              {store.knowledge?.notes?.length
                ? copy.knowledgeChangeWithChat
                : copy.knowledgeWithChat}
            </button>
          )}
        </div>
        <StoreProfileCard profile={store.knowledge?.profile} onChat={onProfile} />
        {store.knowledge?.notes?.length ? (
          <ul className="sf-notes">
            {store.knowledge.notes.map((note) => (
              <li key={note.id} className="sf-note">
                <div className="sf-note-head">
                  <b data-testid="sf-knowledge-note-title">{note.title}</b>
                </div>
                <p className="sf-note-text" data-clamp="true">
                  {note.text}
                </p>
                <NoteSource source={note.source} />
              </li>
            ))}
          </ul>
        ) : (
          <p className="sf-knows-empty">{copy.knowledgeEmpty}</p>
        )}
        {store.information && (
          <InformationPages
            information={store.information}
            onChat={onPages}
            onUpdate={onUpdatePages}
          />
        )}
      </section>

      <footer className={failed ? 'sf-foot sf-foot-stopped' : 'sf-foot'}>
        {!confirming && (
          <p>{reading ? copy.closeNote : failed || store.live ? '' : copy.readyNote}</p>
        )}
        {reading && (
          <button className="ws-button" data-testid="sf-stop" onClick={stop} disabled={stopping}>
            {stopping ? copy.stopping : copy.stopRun}
          </button>
        )}
        {/* Every read is paid, so every read, the retry after a stopped one too, asks first. */}
        {!reading && !confirming && (
          <>
            {failed && onFixRead && (
              <button
                className="ws-button quiet"
                data-testid="sf-fix-in-chat"
                disabled={rechecking}
                onClick={() => onFixRead(stoppedStep ? (stepName[stoppedStep.id] ?? null) : null)}
              >
                <WorkspaceIcon name="chat" size={14} />
                {copy.fixWithChat}
              </button>
            )}
            <button
              ref={opener}
              className={usable || canCheckAgain ? 'ws-button' : 'ws-button primary'}
              data-testid={failed ? 'sf-retry' : 'sf-read-again'}
              disabled={rechecking}
              onClick={() => setConfirming(true)}
            >
              {copy.readAgain}
            </button>
            {usable && (
              <button
                className={canCheckAgain ? 'ws-button' : 'ws-button primary'}
                data-testid="sf-try-widget"
                disabled={rechecking}
                onClick={onTryWidget}
              >
                {copy.buildWidget}
              </button>
            )}
            {canCheckAgain && (
              <button
                className="ws-button primary"
                data-testid="sf-check-again"
                disabled={rechecking}
                aria-busy={rechecking || undefined}
                onClick={checkAgain}
              >
                {rechecking ? copy.checkingAgain : copy.checkAgain}
              </button>
            )}
          </>
        )}
        {confirming && (
          <div
            className="sf-confirm"
            role="group"
            aria-label={copy.readAgain}
            data-testid="sf-read-again-confirm"
          >
            <b>
              {formatCopy(copy.readAgainConfirm, { name: store.name })}
              {store.live ? ` ${copy.readAgainLive}` : ''}
            </b>
            <div>
              <button
                className="ws-button primary"
                data-testid="sf-read-again-now"
                disabled={retrying}
                onClick={retry}
                autoFocus
              >
                {retrying ? copy.retrying : copy.readAgainNow}
              </button>
              <button className="ws-button" disabled={retrying} onClick={cancelConfirm}>
                {copy.notNow}
              </button>
            </div>
          </div>
        )}
      </footer>
    </div>
  );
}

/** "Svenska, English" in the reader's language, from the market's language codes. */
function languageNames(codes: string[], locale: string): string {
  let names: Intl.DisplayNames | null = null;
  try {
    names = new Intl.DisplayNames([locale], { type: 'language' });
  } catch {
    names = null;
  }
  return codes.map((code) => names?.of(code) ?? code).join(', ');
}

/** A finished read's steps fold into one line; a running or stopped read shows them all. */
function StepsFold({
  done,
  summary,
  children,
}: {
  done: boolean;
  summary: string;
  children: ReactNode;
}) {
  return done ? (
    <details className="sf-steps-fold" data-testid="sf-steps-fold">
      <summary>{summary}</summary>
      {children}
    </details>
  ) : (
    <>{children}</>
  );
}
